import { useCallback, useEffect, useRef, useState } from 'react';

import {
  elapsedSecOnTimeline,
  lapWindowAtTimecode,
  timecodeOnTimeline,
  type ReplayTimeline,
} from './telemetrySession';

const playheadTickListeners = new Set<() => void>();
const playheadUiTickListeners = new Set<() => void>();
let lastPlayheadUiNotifyPerf = 0;
/** React HUD/bar readouts — lower rate than map cursor to avoid main-thread jank. */
const PLAYHEAD_UI_NOTIFY_MS = 33;

export function subscribeToPlayheadTicks(listener: () => void) {
  playheadTickListeners.add(listener);
  return () => {
    playheadTickListeners.delete(listener);
  };
}

export function subscribeToPlayheadUiTicks(listener: () => void) {
  playheadUiTickListeners.add(listener);
  return () => {
    playheadUiTickListeners.delete(listener);
  };
}

function notifyPlayheadTick(now = performance.now()) {
  for (const listener of playheadTickListeners) {
    listener();
  }

  if (now - lastPlayheadUiNotifyPerf >= PLAYHEAD_UI_NOTIFY_MS) {
    lastPlayheadUiNotifyPerf = now;
    for (const listener of playheadUiTickListeners) {
      listener();
    }
  }
}

export type TelemetryReplayControls = {
  playheadTimecodeMs: number;
  elapsedSec: number;
  durationSec: number;
  activeLapNumber: number | null;
  isPlaying: boolean;
  playbackRate: number;
  loopStartSec: number | null;
  loopEndSec: number | null;
  loopEnabled: boolean;
  setPlaybackRate: (rate: number) => void;
  setLoopStart: (elapsedSec?: number) => void;
  setLoopEnd: (elapsedSec?: number) => void;
  toggleLoop: () => void;
  clearLoop: () => void;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  restart: () => void;
  seekElapsed: (elapsedSec: number) => void;
  seekTimecode: (timecodeMs: number) => void;
  reset: () => void;
  getPlayheadTimecodeMs: () => number;
  getElapsedSec: () => number;
};

export function useTelemetryReplay(timeline: ReplayTimeline | null): TelemetryReplayControls {
  const [playheadTimecodeMs, setPlayheadTimecodeMs] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [loopStartSec, setLoopStartSec] = useState<number | null>(null);
  const [loopEndSec, setLoopEndSec] = useState<number | null>(null);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const playheadRef = useRef(0);
  const loopStartSecRef = useRef<number | null>(null);
  const loopEndSecRef = useRef<number | null>(null);
  const loopEnabledRef = useRef(false);
  const timelineRef = useRef(timeline);
  const timelineAnchorKeyRef = useRef<string | null>(null);

  useEffect(() => {
    timelineRef.current = timeline;
  }, [timeline]);

  const durationSec = timeline?.durationSec ?? 0;
  const elapsedSec = timeline ? elapsedSecOnTimeline(timeline, playheadTimecodeMs) : 0;
  const activeLapNumber = timeline ? lapWindowAtTimecode(timeline, playheadTimecodeMs)?.lapNumber ?? null : null;

  const seekTimecode = useCallback(
    (timecodeMs: number) => {
      const activeTimeline = timelineRef.current;
      if (!activeTimeline) {
        return;
      }

      const clamped = Math.min(
        Math.max(timecodeMs, activeTimeline.startTimecodeMs),
        activeTimeline.endTimecodeMs,
      );
      playheadRef.current = clamped;
      setPlayheadTimecodeMs(clamped);
      notifyPlayheadTick();
    },
    [],
  );

  const seekElapsed = useCallback(
    (targetSec: number) => {
      const activeTimeline = timelineRef.current;
      if (!activeTimeline) {
        return;
      }

      seekTimecode(timecodeOnTimeline(activeTimeline, targetSec));
    },
    [seekTimecode],
  );

  const reset = useCallback(() => {
    const activeTimeline = timelineRef.current;
    loopStartSecRef.current = null;
    loopEndSecRef.current = null;
    loopEnabledRef.current = false;
    setLoopStartSec(null);
    setLoopEndSec(null);
    setLoopEnabled(false);
    if (!activeTimeline) {
      playheadRef.current = 0;
      setPlayheadTimecodeMs(0);
      setIsPlaying(false);
      notifyPlayheadTick();
      return;
    }

    playheadRef.current = activeTimeline.startTimecodeMs;
    setPlayheadTimecodeMs(activeTimeline.startTimecodeMs);
    setIsPlaying(false);
    notifyPlayheadTick();
  }, []);

  const setLoopStart = useCallback((elapsedSec?: number) => {
    const activeTimeline = timelineRef.current;
    if (!activeTimeline) return;
    const nextStart = elapsedSec === undefined
      ? elapsedSecOnTimeline(activeTimeline, playheadRef.current)
      : Math.min(activeTimeline.durationSec, Math.max(0, elapsedSec));
    loopStartSecRef.current = nextStart;
    setLoopStartSec(nextStart);
    if (loopEndSecRef.current !== null && loopEndSecRef.current <= nextStart) {
      loopEndSecRef.current = null;
      loopEnabledRef.current = false;
      setLoopEndSec(null);
      setLoopEnabled(false);
    }
  }, []);

  const setLoopEnd = useCallback((elapsedSec?: number) => {
    const activeTimeline = timelineRef.current;
    if (!activeTimeline) return;
    const current = elapsedSec === undefined
      ? elapsedSecOnTimeline(activeTimeline, playheadRef.current)
      : Math.min(activeTimeline.durationSec, Math.max(0, elapsedSec));
    const start = loopStartSecRef.current ?? 0;
    const nextEnd = Math.min(activeTimeline.durationSec, Math.max(start + 0.1, current));
    loopStartSecRef.current = start;
    loopEndSecRef.current = nextEnd;
    loopEnabledRef.current = nextEnd > start;
    setLoopStartSec(start);
    setLoopEndSec(nextEnd);
    setLoopEnabled(nextEnd > start);
  }, []);

  const toggleLoop = useCallback(() => {
    if (loopStartSecRef.current === null || loopEndSecRef.current === null) return;
    const next = !loopEnabledRef.current;
    loopEnabledRef.current = next;
    setLoopEnabled(next);
  }, []);

  const clearLoop = useCallback(() => {
    loopStartSecRef.current = null;
    loopEndSecRef.current = null;
    loopEnabledRef.current = false;
    setLoopStartSec(null);
    setLoopEndSec(null);
    setLoopEnabled(false);
  }, []);

  const play = useCallback(() => {
    const activeTimeline = timelineRef.current;
    if (!activeTimeline || activeTimeline.durationSec <= 0) {
      return;
    }

    const loopStart = loopStartSecRef.current;
    const loopEnd = loopEndSecRef.current;
    const currentElapsed = elapsedSecOnTimeline(activeTimeline, playheadRef.current);
    if (
      loopEnabledRef.current
      && loopStart !== null
      && loopEnd !== null
      && (currentElapsed < loopStart || currentElapsed >= loopEnd)
    ) {
      seekTimecode(timecodeOnTimeline(activeTimeline, loopStart));
    } else if (
      playheadRef.current >= activeTimeline.endTimecodeMs ||
      playheadRef.current < activeTimeline.startTimecodeMs
    ) {
      seekTimecode(activeTimeline.startTimecodeMs);
    }

    setIsPlaying(true);
  }, [seekTimecode]);

  const pause = useCallback(() => {
    setIsPlaying(false);
    setPlayheadTimecodeMs(playheadRef.current);
    notifyPlayheadTick();
  }, []);

  const getPlayheadTimecodeMs = useCallback(() => playheadRef.current, []);

  const getElapsedSec = useCallback(() => {
    const activeTimeline = timelineRef.current;
    return activeTimeline ? elapsedSecOnTimeline(activeTimeline, playheadRef.current) : 0;
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      pause();
      return;
    }

    play();
  }, [isPlaying, pause, play]);

  const restart = useCallback(() => {
    const activeTimeline = timelineRef.current;
    if (!activeTimeline) {
      return;
    }

    seekTimecode(activeTimeline.startTimecodeMs);
    setIsPlaying(true);
  }, [seekTimecode]);

  const timelineAnchorKey = timeline
    ? `${timeline.scope}:${timeline.startTimecodeMs}`
    : null;

  useEffect(() => {
    if (timelineAnchorKeyRef.current === timelineAnchorKey) {
      if (timeline && playheadRef.current > timeline.endTimecodeMs) {
        playheadRef.current = timeline.endTimecodeMs;
        setPlayheadTimecodeMs(timeline.endTimecodeMs);
        notifyPlayheadTick();
      }
      return;
    }

    timelineAnchorKeyRef.current = timelineAnchorKey;
    reset();
  }, [reset, timeline, timelineAnchorKey]);

  useEffect(() => {
    const playbackTimeline = timelineRef.current;
    if (!isPlaying || !playbackTimeline) {
      return;
    }

    let frame = 0;
    const playStartPerf = performance.now();
    const playStartPlayheadMs = playheadRef.current;

    const tick = (now: number) => {
      const elapsedMs = (now - playStartPerf) * playbackRate;
      const activeLoopStartSec = loopStartSecRef.current;
      const activeLoopEndSec = loopEndSecRef.current;
      const loopActive = loopEnabledRef.current
        && activeLoopStartSec !== null
        && activeLoopEndSec !== null
        && activeLoopEndSec > activeLoopStartSec;
      const loopStartMs = loopActive ? timecodeOnTimeline(playbackTimeline, activeLoopStartSec) : 0;
      const loopEndMs = loopActive ? timecodeOnTimeline(playbackTimeline, activeLoopEndSec) : 0;
      const loopDurationMs = loopEndMs - loopStartMs;
      const next = loopActive
        ? loopStartMs + ((Math.max(loopStartMs, playStartPlayheadMs) - loopStartMs + elapsedMs) % loopDurationMs)
        : Math.min(playbackTimeline.endTimecodeMs, playStartPlayheadMs + elapsedMs);
      playheadRef.current = next;

      if (!loopActive && next >= playbackTimeline.endTimecodeMs) {
        setPlayheadTimecodeMs(next);
        setIsPlaying(false);
        notifyPlayheadTick();
        return;
      }

      notifyPlayheadTick();
      frame = window.requestAnimationFrame(tick);
    };

    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [isPlaying, loopEnabled, loopEndSec, loopStartSec, playbackRate]);

  return {
    playheadTimecodeMs,
    elapsedSec,
    durationSec,
    activeLapNumber,
    isPlaying,
    playbackRate,
    loopStartSec,
    loopEndSec,
    loopEnabled,
    setPlaybackRate,
    setLoopStart,
    setLoopEnd,
    toggleLoop,
    clearLoop,
    play,
    pause,
    togglePlay,
    restart,
    seekElapsed,
    seekTimecode,
    reset,
    getPlayheadTimecodeMs,
    getElapsedSec,
  };
}
